/**
 * ActivityAttachmentsDisplay - Exibe anexos de uma atividade na timeline
 * Estilo visual inspirado no Bitrix24 com grid de imagens e lista de arquivos
 * 
 * Versão: 1.0.0
 * Data: 2026-01-04
 */
import React, { useState } from 'react';
import { 
  FileText, 
  FileSpreadsheet, 
  File,
  Download,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getThumbnailUrl } from '@/lib/storage';
import { formatFileSize } from '@/hooks/useActivityAttachments';
import type { ActivityAttachment } from '@/hooks/useActivityAttachments';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Props do componente
interface ActivityAttachmentsDisplayProps {
  attachments: ActivityAttachment[];
  compact?: boolean;
}

// Ícones por tipo de arquivo
const fileTypeIcons: Record<string, typeof FileText> = {
  pdf: FileText,
  document: FileText,
  spreadsheet: FileSpreadsheet,
  other: File,
};

/**
 * Componente para exibir anexos na timeline
 */
export function ActivityAttachmentsDisplay({ 
  attachments, 
  compact = false 
}: ActivityAttachmentsDisplayProps) {
  // Estado do lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Separa imagens de outros arquivos
  const images = attachments.filter(a => a.file_type === 'image');
  const files = attachments.filter(a => a.file_type !== 'image');

  // Configuração do grid de imagens
  const maxVisibleImages = compact ? 4 : 10;
  const hiddenCount = Math.max(0, images.length - maxVisibleImages);

  // Calcula tamanho total dos arquivos
  const totalSize = attachments.reduce((acc, a) => acc + (a.file_size || 0), 0);

  // Abre lightbox em uma imagem específica
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Navega no lightbox
  const navigateLightbox = (direction: 'prev' | 'next') => {
    setLightboxIndex(prev => {
      if (direction === 'prev') {
        return prev === 0 ? images.length - 1 : prev - 1;
      }
      return prev === images.length - 1 ? 0 : prev + 1;
    });
  };

  // Se não há anexos, não renderiza
  if (attachments.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Grid de Imagens */}
      {images.length > 0 && (
        <div className={cn(
          "grid gap-1.5",
          compact ? "grid-cols-4 max-w-[280px]" : "grid-cols-5 max-w-[400px]"
        )}>
          {images.slice(0, maxVisibleImages).map((img, idx) => (
            <div 
              key={img.id} 
              className={cn(
                "relative aspect-square rounded-md overflow-hidden cursor-pointer",
                "ring-1 ring-border hover:ring-primary/50 transition-all",
                "group",
                compact ? "max-w-[64px] max-h-[64px]" : "max-w-[72px] max-h-[72px]"
              )}
              onClick={() => openLightbox(idx)}
            >
              {/* Thumbnail otimizada */}
              <img 
                src={getThumbnailUrl(img.public_url, 128, 128)}
                alt={img.file_name}
                loading="lazy"
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              
              {/* Contador +N na última imagem visível */}
              {idx === maxVisibleImages - 1 && hiddenCount > 0 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    +{hiddenCount}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lista de Arquivos (não-imagens) */}
      {files.length > 0 && (
        <div className="space-y-1">
          <span className="text-[10px] text-muted-foreground font-medium">
            Arquivos:
          </span>
          
          <div className="space-y-0.5">
            {files.map(file => {
              const Icon = fileTypeIcons[file.file_type] || File;
              
              return (
                <div 
                  key={file.id} 
                  className="flex items-center gap-2 text-xs group"
                >
                  <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  
                  <a 
                    href={file.public_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate flex-1"
                    title={file.file_name}
                  >
                    {file.file_name}
                  </a>
                  
                  <span className="text-[10px] text-muted-foreground/70 flex-shrink-0">
                    {formatFileSize(file.file_size)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Link para baixar todos */}
          {attachments.length > 1 && (
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                // Abre cada arquivo em nova aba (simples download)
                attachments.forEach(a => window.open(a.public_url, '_blank'));
              }}
              className="text-[10px] text-muted-foreground/70 hover:text-primary flex items-center gap-1"
            >
              <Download className="h-2.5 w-2.5" />
              Baixar todos ({formatFileSize(totalSize)})
            </a>
          )}
        </div>
      )}

      {/* Lightbox para visualização de imagens */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
          <div className="relative w-full h-[80vh] flex items-center justify-center">
            {/* Botão fechar */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 text-white hover:bg-white/20 z-10"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Navegação anterior */}
            {images.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 text-white hover:bg-white/20 z-10"
                onClick={() => navigateLightbox('prev')}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {/* Imagem atual */}
            {images[lightboxIndex] && (
              <img
                src={images[lightboxIndex].public_url}
                alt={images[lightboxIndex].file_name}
                className="max-w-full max-h-full object-contain"
              />
            )}

            {/* Navegação próxima */}
            {images.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 text-white hover:bg-white/20 z-10"
                onClick={() => navigateLightbox('next')}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}

            {/* Contador de imagens */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
              {lightboxIndex + 1} / {images.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
